import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import StructuredSection from '../StructuredSection';
import sectionStyles from '../StructuredSections.module.scss';
import { buildSlug, clampHeadingLevel, joinClassNames } from '../utils';
import { ProcessedSection } from '../sectionProcessing';
import { PIMCORE_CONTENT_BASE_URL } from '../../../../utils/Pimcore';
import { renderSectionContent } from './SitePageSectionContent';
import { resolveSitePageLink } from './SitePageLinkResolver';

export interface SectionGroup {
    id: string;
    sections: ProcessedSection[];
    background: 'gray' | 'white';
    mergeWithPrevious: boolean;
}

interface SitePageSectionGroupProps {
    group: SectionGroup;
}

export const SitePageSectionGroup: React.FC<SitePageSectionGroupProps> = ({ group }) => {
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;

    return (
        <StructuredSection background={group.background} merged={group.mergeWithPrevious}>
            <div className={sectionStyles.groupContent}>
                {group.sections.map((section) => {
                    const HeadingTag =
                        `h${clampHeadingLevel(section.level)}` as keyof JSX.IntrinsicElements;
                    const headingId = buildSlug(section.title, section.id);
                    const contentNode = renderSectionContent(section);
                    const imageClassName = joinClassNames(
                        sectionStyles.groupImage,
                        section.image && section.image.position === 'left'
                            ? sectionStyles.groupImageLeft
                            : sectionStyles.groupImageRight
                    );
                    const imageSrc = section.image?.url
                        ? `${PIMCORE_CONTENT_BASE_URL}${section.image.url}`
                        : null;

                    let figureNode: React.ReactNode = null;

                    if (imageSrc) {
                        const imageLink = section.image?.link ?? null;
                        const resolvedImageLink = imageLink
                            ? resolveSitePageLink(imageLink, currentOrigin)
                            : null;
                        const shouldRenderLink =
                            resolvedImageLink?.href && resolvedImageLink.href.length > 0;
                        const linkTarget =
                            imageLink && imageLink.openInNewTab ? '_blank' : undefined;
                        const linkRel =
                            imageLink && imageLink.openInNewTab ? 'noopener noreferrer' : undefined;
                        const imageNode = (
                            <img
                                src={imageSrc}
                                alt={section.image?.alt ?? ''}
                                title={section.image?.title ?? ''}
                            />
                        );
                        const captionNode = section.image?.caption ? (
                            <figcaption className={sectionStyles.groupCaption}>
                                <span>{section.image.caption}</span>
                            </figcaption>
                        ) : null;

                        const linkedImageNode = shouldRenderLink ? (
                            resolvedImageLink?.isExternal ? (
                                <a href={resolvedImageLink.href!} target={linkTarget} rel={linkRel}>
                                    {imageNode}
                                </a>
                            ) : (
                                <RouterLink
                                    to={resolvedImageLink!.href!}
                                    target={linkTarget}
                                    rel={linkRel}
                                >
                                    {imageNode}
                                </RouterLink>
                            )
                        ) : (
                            imageNode
                        );

                        figureNode = (
                            <>
                                <div className={sectionStyles.sectionBlockClear} />
                                <figure className={imageClassName}>
                                    {linkedImageNode}
                                    {captionNode}
                                </figure>
                            </>
                        );
                    }

                    return (
                        <div key={section.id} className={sectionStyles.sectionBlock} id={headingId}>
                            {figureNode}
                            {section.title && section.title.trim().length > 0 && (
                                <HeadingTag>{section.title}</HeadingTag>
                            )}
                            {contentNode}
                        </div>
                    );
                })}
            </div>
        </StructuredSection>
    );
};
